import { type FC, useState } from 'react';
import cn from 'classnames';
import { motion, useAnimate } from 'framer-motion';

import { usePreloadImages } from '@/common/hooks/preload-images';

import lightPng from '/img/light.png';
import lightLargePng from '/img/light-large.png';

interface RestoreEnergyLightAnimationProps {
  isAnimationStart: boolean;
}

const RestoreEnergyLightAnimation: FC<RestoreEnergyLightAnimationProps> = ({
  isAnimationStart,
}) => {
  const [isNextAnimationStart, setIsNextAnimationStart] = useState(false);
  usePreloadImages([lightPng, lightLargePng]);

  const [largeLightScope, largeLightAnimate] = useAnimate();
  const [flashScope, flashAnimate] = useAnimate();

  const secondAnimation = async (): Promise<void> => {
    setIsNextAnimationStart(true);
    await Promise.all([
      largeLightAnimate(
        largeLightScope.current,
        { opacity: 1 },
        { ease: 'easeOut', duration: 0.1 },
      ),
      flashAnimate(
        flashScope.current,
        { opacity: 1 },
        { ease: 'easeOut', duration: 0.1 },
      ),
    ]);
    await Promise.all([
      largeLightAnimate(
        largeLightScope.current,
        { opacity: 0 },
        { ease: 'easeOut', delay: 0.1, duration: 0.7 },
      ),
      flashAnimate(
        flashScope.current,
        { opacity: 0 },
        { ease: 'easeOut', delay: 0.1, duration: 0.7 },
      ),
    ]);
    setIsNextAnimationStart(false);
  };

  return (
    <>
      {isAnimationStart && (
        <motion.img
          style={{ width: window.innerWidth - 44 }}
          className="absolute top-0 left-1/2 -translate-x-1/2 aspect-[383/364]"
          animate={{ opacity: 0 }}
          transition={{ duration: 1 }}
          onAnimationComplete={secondAnimation}
          src={lightPng}
          alt="small light animation"
        />
      )}
      <motion.img
        src={lightLargePng}
        ref={largeLightScope}
        initial={{ opacity: 0 }}
        style={{ height: window.innerHeight, maxWidth: 'max-content' }}
        className={cn(
          'absolute top-0 left-1/2 -translate-x-1/2 aspect-[975/926] max-w-auto',
          isNextAnimationStart ? 'z-10' : '-z-[1]',
        )}
      />
      <motion.div
        ref={flashScope}
        initial={{ opacity: 0 }}
        className={cn(
          'absolute top-0 left-0 w-full h-full border-solid border-[16px] border-white blur-[32px]',
          isNextAnimationStart ? 'z-10' : '-z-[1]',
        )}
      ></motion.div>
    </>
  );
};

export { RestoreEnergyLightAnimation };
